import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useReveal } from "@/hooks/useReveal";

const products = [
  {
    title: "Fintech Dashboard",
    client: "FinTrack Solutions",
    industry: "Finance",
    duration: "12 weeks",
    description:
      "A comprehensive financial tracking platform for SMEs with real-time analytics, automated reporting, and multi-currency support. Built to handle thousands of transactions per second with bank-grade security.",
    technologies: ["React", "Node.js", "PostgreSQL", "AWS"],
    color: "#D4FF3F",
  },
  {
    title: "Healthcare Platform",
    client: "MedConnect UK",
    industry: "Healthcare",
    duration: "16 weeks",
    description:
      "Patient management system with telemedicine features, appointment scheduling, and secure electronic health records. Fully compliant with GDPR and healthcare data standards.",
    technologies: ["Next.js", "Python", "FHIR API", "Docker"],
    color: "#F7C59F",
  },
  {
    title: "E-Commerce Platform",
    client: "ShopStream Retail",
    industry: "Retail",
    duration: "10 weeks",
    description:
      "Omnichannel retail platform integrating inventory management, POS systems, and customer loyalty programs. Increased client revenue by 34% in the first quarter post-launch.",
    technologies: ["Vue.js", "Go", "Redis", "Kubernetes"],
    color: "#E2724B",
  },
  {
    title: "EdTech LMS",
    client: "LearnSphere Global",
    industry: "Education",
    duration: "14 weeks",
    description:
      "Interactive learning management system with video courses, progress tracking, and AI-powered personalized recommendations. Serving over 50,000 active learners worldwide.",
    technologies: ["React", "Django", "TensorFlow", "GCP"],
    color: "#D4FF3F",
  },
];

const stats = [
  { label: "Projects delivered", value: "150+" },
  { label: "Client retention", value: "92%" },
  { label: "Team members", value: "50+" },
  { label: "Countries served", value: "12" },
];

export default function ProductsPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const listRef = useReveal<HTMLDivElement>();

  return (
    <SiteLayout navbarVariant="overlay">
      {/* Hero */}
      <section className="relative h-[420px] md:h-[560px] overflow-hidden bg-ink">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink" />
        <div
          ref={heroRef}
          className="relative h-full flex flex-col justify-end mx-auto max-w-[1320px] px-6 md:px-10 pb-16"
        >
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-cream/60 mb-4 block">
              Our work
            </span>
            <h1 className="text-display text-5xl md:text-7xl text-cream leading-[0.95]">
              Products we{"'"}ve <span className="italic font-light">built</span>.
            </h1>
            <p className="mt-6 max-w-xl text-cream/70 text-base md:text-lg leading-relaxed">
              Real projects, real results. Explore a selection of platforms and applications
              we have delivered for clients across finance, healthcare, retail, and education.
            </p>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-lime-accent">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-ink/15">
            {stats.map((s) => (
              <div key={s.label} className="px-6 py-5 text-center">
                <div className="text-display text-xl md:text-2xl font-semibold text-ink">
                  {s.value}
                </div>
                <div className="text-[10px] uppercase tracking-widest2 text-ink/60 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products list */}
      <section ref={listRef} className="bg-cream py-20 md:py-28">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="space-y-8">
            {products.map((p, i) => (
              <div
                key={p.title}
                data-reveal
                data-reveal-delay={`${i * 80}`}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-8 rounded-3xl bg-sand/40 border border-ink/5 hover:border-ink/15 transition-all p-8 md:p-10"
              >
                <div className="lg:col-span-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: p.color }}
                    />
                    <span className="text-[10px] uppercase tracking-widest2 text-ink/50 font-semibold">
                      {p.industry} · {p.duration}
                    </span>
                  </div>
                  <h3 className="text-display text-2xl md:text-4xl mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-ink/60 mb-4">
                    Client: {p.client}
                  </p>
                  <p className="text-ink/70 text-sm md:text-base leading-relaxed max-w-2xl">
                    {p.description}
                  </p>
                </div>
                <div className="lg:col-span-4 flex flex-col justify-between">
                  <div className="flex flex-wrap gap-2">
                    {p.technologies.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-ink/5 text-ink/70 px-3 py-1.5 text-[11px] uppercase tracking-widest2"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream pb-20 md:pb-28">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div
            className="relative rounded-[32px] overflow-hidden min-h-[420px] md:min-h-[520px] flex items-end"
            data-reveal
          >
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20modern%20technology%20background%20with%20flowing%20digital%20data%20streams%20geometric%20network%20connections%20warm%20cream%20charcoal%20and%20muted%20green%20tones%20professional%20corporate%20aesthetic%20minimal%20clean&width=1800&height=1100&seq=vantory-products-cta&orientation=landscape"
              alt="Digital solutions"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/30 to-ink/20" />

            <div className="absolute top-10 md:top-14 left-6 md:left-12 right-6 md:right-12">
              <span className="text-xs uppercase tracking-widest2 text-cream/80">
                Have a project in mind?
              </span>
              <h2 className="text-display text-cream text-4xl md:text-6xl leading-[0.95] mt-4 text-balance max-w-2xl">
                Let&apos;s create
                <br />
                something <span className="italic font-light">together</span>.
              </h2>
            </div>

            <div className="relative z-10 p-6 md:p-12 w-full flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 rounded-full bg-cream text-ink pl-7 pr-2 py-2 text-sm font-semibold uppercase tracking-widest2 cursor-pointer whitespace-nowrap hover:bg-lime-accent transition-colors"
              >
                Start your project
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-ink text-cream group-hover:rotate-[-45deg] transition-transform duration-300">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </Link>
              <div className="text-cream/85 text-sm md:text-base max-w-sm text-left md:text-right">
                From concept to launch — we handle every step of product development.
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}